import React, { useEffect } from 'react';
import { Mountain, ArrowLeft, ExternalLink, ShoppingBag, Tag, StickyNote, Eye, Wifi, RefreshCw, Gift, Scale, List } from 'lucide-react';
import { Link } from 'react-router-dom';

export const RoamShopping: React.FC = () => {
  const chromeStoreUrl = 'https://chromewebstore.google.com/detail/roam-shopping/dianmjnphbbemaokcobdljfknifpfdpc';

  useEffect(() => {
    document.title = 'Roam Shopping | Save & Track Products While Browsing';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Roam Shopping is a Chrome extension that helps you save products, mark your interest level, and add notes so you never have to start your research over.'
      );
    }
  }, []);

  const features = [
    {
      icon: ShoppingBag,
      title: 'Save Products While Browsing',
      description: 'Clip any product from any website with one click and keep it in your personal shopping list.'
    },
    {
      icon: Tag,
      title: 'Mark Your Interest Level',
      description: 'Rate each product as No, Maybe, or Yes so you always know where you stand on an item.'
    },
    {
      icon: StickyNote,
      title: 'Add Notes That Actually Help',
      description: 'Record exactly why you saved, skipped, or ruled out an item — "too expensive," "wrong size," "good gift idea," "compare with the other model."'
    },
    {
      icon: Eye,
      title: 'Recognize Already-Considered Products',
      description: 'Saved products are annotated directly in search results so you never waste time re-evaluating something you already decided on.'
    },
    {
      icon: List,
      title: 'Organize With Tags',
      description: 'Group saved products however you like — by category, project, person, or occasion.'
    },
    {
      icon: Wifi,
      title: 'Works Offline, No Account Required',
      description: 'Everything is stored locally on your device by default. No sign-up needed to get started.'
    },
    {
      icon: RefreshCw,
      title: 'Optional Sync Across Devices',
      description: 'Sign in to enable sync and access your saved products from any device.'
    }
  ];

  const useCases = [
    {
      icon: Scale,
      title: 'Comparison Shopping',
      description: 'Keep track of competing products, your notes on each, and your verdict — all in one place.'
    },
    {
      icon: Gift,
      title: 'Gift Planning',
      description: 'Build gift lists for anyone. Add notes about why an item is a good fit and come back when you\'re ready to buy.'
    },
    {
      icon: List,
      title: 'Wish Lists',
      description: 'Save items you want for yourself without losing them across browser tabs and sessions.'
    },
    {
      icon: ShoppingBag,
      title: 'Any Buying Decision',
      description: 'Any time you don\'t want to rethink a purchase from scratch, Roam Shopping has you covered.'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-purple-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Link to="/" className="flex items-center space-x-3">
              <div className="bg-gradient-to-br from-blue-500 to-purple-600 p-2 rounded-xl">
                <Mountain className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Roam</h1>
                <p className="text-sm text-gray-600">Outdoor Adventure Apps</p>
              </div>
            </Link>

            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/" className="text-gray-700 hover:text-purple-600 transition-colors">
                Home
              </Link>
              <a
                href={chromeStoreUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
              >
                Add to Chrome
                <ExternalLink className="h-4 w-4" />
              </a>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-20 sm:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Link
            to="/"
            className="inline-flex items-center text-purple-600 hover:text-purple-700 transition-colors mb-10"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Link>

          <div className="flex justify-center mb-8">
            <img
              src="/shopping-icon.png"
              alt="Roam Shopping icon"
              className="h-28 w-28 rounded-3xl shadow-xl"
            />
          </div>

          <div className="inline-flex items-center bg-purple-100 text-purple-700 px-4 py-1.5 rounded-full text-sm font-medium mb-6">
            Chrome Extension &nbsp;·&nbsp; 100% Free
          </div>

          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            Remember every product<br className="hidden sm:block" /> you ever considered
          </h2>

          <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto leading-relaxed">
            Roam Shopping helps you save products, mark your interest level, and remember why you felt that way — so you never have to start your research over.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={chromeStoreUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-4 rounded-xl font-semibold text-lg flex items-center justify-center gap-2 transition-all shadow-lg hover:shadow-xl"
            >
              Add to Chrome — It's Free
              <ExternalLink className="h-5 w-5" />
            </a>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Everything you need to shop smarter
            </h3>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Leave notes on products while you shop, so the next time you see them you remember whether you already said no, maybe, or yes.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="bg-gradient-to-br from-slate-50 to-purple-50 rounded-2xl p-6 border border-purple-100">
                  <div className="bg-gradient-to-r from-purple-500 to-violet-500 p-3 rounded-xl w-fit mb-4">
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h4>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Perfect for any buying decision
            </h3>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Any time you don't want to rethink a purchase from scratch, Roam Shopping has you covered.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {useCases.map((useCase, index) => {
              const Icon = useCase.icon;
              return (
                <div key={index} className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 flex items-start gap-4">
                  <div className="bg-gradient-to-r from-purple-500 to-violet-500 p-3 rounded-xl flex-shrink-0">
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h4 className="text-xl font-semibold text-gray-900 mb-2">{useCase.title}</h4>
                    <p className="text-gray-600 leading-relaxed">{useCase.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Privacy / Offline callout */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-purple-600 to-violet-600 rounded-3xl p-10 sm:p-14 text-center text-white">
            <Wifi className="h-10 w-10 mx-auto mb-4 opacity-90" />
            <h3 className="text-3xl font-bold mb-4">Works offline. No account needed.</h3>
            <p className="text-purple-100 text-lg mb-8 max-w-xl mx-auto leading-relaxed">
              Your data stays on your device by default. Sign in only when you want to sync across devices. Roam Shopping is 100% free — no subscription, no ads.
            </p>
            <a
              href={chromeStoreUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white text-purple-700 hover:bg-purple-50 px-8 py-4 rounded-xl font-semibold text-lg inline-flex items-center gap-2 transition-all shadow-lg"
            >
              Add to Chrome — It's Free
              <ExternalLink className="h-5 w-5" />
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row justify-between items-center gap-4">
          <Link to="/" className="flex items-center space-x-3">
            <div className="bg-gradient-to-br from-blue-500 to-purple-600 p-2 rounded-xl">
              <Mountain className="h-6 w-6 text-white" />
            </div>
            <span className="text-lg font-semibold">Roam Apps</span>
          </Link>
          <div className="flex gap-6 text-sm text-gray-400">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <Link to="/support" className="hover:text-white transition-colors">Support</Link>
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
          </div>
          <p className="text-gray-500 text-sm">© 2026 Roam. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};
